import { useCallback, useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import useAssistantPanelStore from "./useStore";
import {
  FLOATING_MIN_WIDTH,
  FLOATING_MAX_WIDTH,
  FLOATING_MIN_HEIGHT,
  FLOATING_MAX_HEIGHT,
} from "./constants";
type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const useFloatingResize = () => {
  const { floatingPosition, floatingSize, setFloatingPosition, setFloatingSize } =
    useAssistantPanelStore(
      useShallow((state) => ({
        floatingPosition: state.floatingPosition,
        floatingSize: state.floatingSize,
        setFloatingPosition: state.setFloatingPosition,
        setFloatingSize: state.setFloatingSize,
      }))
    );

  const resizeDirection = useRef<ResizeDirection | null>(null);
  const startPos = useRef({ x: 0, y: 0 });
  const initialPosition = useRef({ x: 0, y: 0 });
  const initialSize = useRef({ width: 0, height: 0 });
  const rafId = useRef<number | null>(null);
  const pointerId = useRef<number | null>(null);
  const targetElement = useRef<HTMLElement | null>(null);

  const onStart = useCallback(
    (direction: ResizeDirection) => (e: React.PointerEvent) => {
      if (e.button !== 0 || e.isPrimary === false) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      pointerId.current = e.pointerId;
      targetElement.current = target;

      resizeDirection.current = direction;
      startPos.current = { x: e.clientX, y: e.clientY };
      initialPosition.current = { x: floatingPosition.x, y: floatingPosition.y };
      initialSize.current = { width: floatingSize.width, height: floatingSize.height };
      document.body.style.userSelect = "none";
    },
    [floatingPosition, floatingSize]
  );

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      const direction = resizeDirection.current;
      if (!direction) return;

      e.preventDefault();

      if (rafId.current) return;

      rafId.current = requestAnimationFrame(() => {
        const deltaX = e.clientX - startPos.current.x;
        const deltaY = e.clientY - startPos.current.y;

        let newWidth = initialSize.current.width;
        let newHeight = initialSize.current.height;
        let newX = initialPosition.current.x;
        let newY = initialPosition.current.y;

        // Handle horizontal resize
        if (direction.includes("e")) {
          newWidth = Math.max(
            FLOATING_MIN_WIDTH,
            Math.min(FLOATING_MAX_WIDTH, initialSize.current.width + deltaX)
          );
        }
        if (direction.includes("w")) {
          const widthDelta = -deltaX;
          newWidth = Math.max(
            FLOATING_MIN_WIDTH,
            Math.min(FLOATING_MAX_WIDTH, initialSize.current.width + widthDelta)
          );
          // Adjust position when resizing from left
          newX = initialPosition.current.x - (newWidth - initialSize.current.width);
        }

        // Handle vertical resize
        if (direction.includes("s")) {
          newHeight = Math.max(
            FLOATING_MIN_HEIGHT,
            Math.min(FLOATING_MAX_HEIGHT, initialSize.current.height + deltaY)
          );
        }
        if (direction.includes("n")) {
          const heightDelta = -deltaY;
          newHeight = Math.max(
            FLOATING_MIN_HEIGHT,
            Math.min(FLOATING_MAX_HEIGHT, initialSize.current.height + heightDelta)
          );
          // Adjust position when resizing from top
          newY = initialPosition.current.y - (newHeight - initialSize.current.height);
        }

        setFloatingSize({ width: newWidth, height: newHeight });
        setFloatingPosition({ x: newX, y: newY });
        rafId.current = null;
      });
    };

    const handleEnd = () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      if (resizeDirection.current) {
        if (targetElement.current && pointerId.current !== null) {
          targetElement.current.releasePointerCapture(pointerId.current);
          pointerId.current = null;
          targetElement.current = null;
        }

        resizeDirection.current = null;
        document.body.style.userSelect = "";
      }
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleEnd);
    document.addEventListener("pointercancel", handleEnd);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleEnd);
      document.removeEventListener("pointercancel", handleEnd);
    };
  }, [setFloatingPosition, setFloatingSize]);

  return { onStart };
};

export default useFloatingResize;
